<template>
  <div class="thumb-preview" data-test="thumb-preview">
    <div v-if="loading" class="thumb-loading" data-test="thumb-loading">
      <b-spinner small variant="secondary" />
    </div>

    <!-- PDF: the first page, drawn in the browser. -->
    <component
      :is="pdfComponent"
      v-else-if="kind === 'pdf' && pdfSource && pdfComponent"
      :source="pdfSource"
      :page="1"
      :width="width"
      class="thumb-pdf"
      data-test="thumb-pdf"
      @rendering-failed="fail"
      @loading-failed="fail"
    />

    <!-- Word: the start of the document as simple, cleaned HTML. -->
    <!-- eslint-disable-next-line vue/no-v-html -->
    <div
      v-else-if="kind === 'doc' && docHtml"
      class="thumb-doc"
      data-test="thumb-doc"
      v-html="docHtml"
    ></div>

    <spreadsheet-thumb-preview
      v-else-if="kind === 'xls' && file"
      :file="file"
      :content-path="contentPath"
      data-test="thumb-sheet"
    />
  </div>
</template>

<script>
import SpreadsheetThumbPreview from '@/components/files/SpreadsheetThumbPreview'
import { docxToSafeHtml } from '@/utils/docxPreview'
import loadPdfEmbed from '@/utils/loadPdfEmbed'

/**
 * A picture of the file's first page for the preview drawer: a PDF's first
 * page, the start of a Word document, or the first rows of a spreadsheet.
 * Everything is read through the API (the storage bucket has no CORS policy for
 * a browser fetch) and drawn in the browser, so it works for older uploads that
 * never had a server-made thumbnail. Emits `failed` when it cannot, so the
 * drawer can show its plain file-type tile instead.
 */
export default {
  components: { SpreadsheetThumbPreview },

  props: {
    // The file's first attachment: { id, file_name, mime_type }.
    file: { type: Object, default: null },
    // 'pdf' | 'doc' | 'xls' (the list's file kinds).
    kind: { type: String, default: '' },
    width: { type: Number, default: 300 },
    // Where the bytes are read from; a company file's media by default.
    contentPath: { type: String, default: '' },
  },

  data() {
    return {
      loading: false,
      pdfComponent: null,
      pdfSource: null,
      docHtml: '',
    }
  },

  watch: {
    file: { immediate: true, handler: 'load' },
    kind: 'load',
  },

  methods: {
    fail() {
      this.$emit('failed')
    },

    async load() {
      this.pdfSource = null
      this.docHtml = ''
      if (!this.file || !this.file.id) return this.fail()
      if (this.kind === 'xls') return
      if (this.kind !== 'pdf' && this.kind !== 'doc') return this.fail()

      const request = (this.request = (this.request || 0) + 1)
      this.loading = true
      try {
        const path = this.contentPath || `media/${this.file.id}/content`
        const { data } = await this.$api.get(path, {
          responseType: 'arraybuffer',
        })
        if (request !== this.request) return

        if (this.kind === 'pdf') {
          if (!this.pdfComponent) {
            this.pdfComponent = await loadPdfEmbed()
          }
          if (request !== this.request) return
          this.pdfSource = new Uint8Array(data)
        } else {
          const html = await docxToSafeHtml(data)
          if (request !== this.request) return
          if (!html) throw new Error('empty document')
          this.docHtml = html
        }
      } catch (error) {
        if (request === this.request) this.fail()
      } finally {
        if (request === this.request) this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.thumb-preview {
  width: 100%;
  display: flex;
  justify-content: center;
}

.thumb-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 160px;
}

.thumb-pdf {
  max-width: 100%;
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(17, 24, 39, 0.08);
}

/* A paper-like sheet showing only the top of the document, fading out below. */
.thumb-doc {
  position: relative;
  width: 100%;
  max-height: 300px;
  padding: 16px 18px;
  overflow: hidden;
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(17, 24, 39, 0.08);
  color: #111827;
  font-size: 11px;
  line-height: 1.4;
  text-align: left;
  -webkit-mask-image: linear-gradient(#000 70%, transparent);
  mask-image: linear-gradient(#000 70%, transparent);
}

.thumb-doc /deep/ h1,
.thumb-doc /deep/ h2,
.thumb-doc /deep/ h3,
.thumb-doc /deep/ h4 {
  margin: 0 0 6px;
  font-size: 13px;
  font-weight: 700;
}

.thumb-doc /deep/ p {
  margin: 0 0 6px;
}

.thumb-doc /deep/ table {
  width: 100%;
  font-size: 10px;
}

.thumb-doc /deep/ img {
  max-width: 100%;
}
</style>
